import type { BidStrategy, Placement, PlacementProblem } from "./line-item-fields.ts";
/**
 * The words beside the ad group's goal, bid and placements in the wizard.
 *
 * Every sentence that says what X does quotes X's reference or guide (saved in
 * `audits/2026-10-23/x-docs/`, cited R:/G: by line), and every sentence that says what this
 * console does says it as the console's own choice. Labels follow Ads Manager's form —
 * «Optimization goal», «Pay by», «Bid strategy», «Placements» — because the owner checks the
 * result there (audits/2026-10-23/adgroup-fields-spec.md, acceptance).
 *
 * Rendered by the wizard's ad-group step (pages/CreateAd.tsx) since PR 5's wiring, the commit
 * that also rewrote `OBJECTIVE_NOTE`, `FIXED_SHAPE` and `PRICE_IS_X` in line-item-copy.ts: until
 * the controls were on the screen those sentences were true, and they said the placement and
 * the bid were fixed.
 */
import { microMoney } from "./money.ts";

// ── Optimization goal ───────────────────────────────────────────────

export const GOAL_LABEL = "Optimization goal";

/** The first option: no goal is sent, and X picks one. */
export const GOAL_DEFAULT_OPTION = "X's default for this objective (not sent)";

export const GOAL_HELP =
	"X's reference lists sixteen goals and says the default is «based on objective», but it has no table of which goal goes with which objective. This console offers what a line of the reference ties to the objective, or what X itself set on this account; leaving the goal out lets X choose, as Ads Manager does.";

export const GOAL_NAMES: Readonly<Record<string, string>> = {
	SITE_VISITS: "SITE_VISITS — X: «only available with the WEBSITE_CLICKS objective»",
	LINK_CLICKS:
		"LINK_CLICKS — Ads Manager's «Link clicks»; X chose it itself for this console's WEBSITE_CLICKS ad group on 2026-10-23",
};

// ── Pay by ──────────────────────────────────────────────────────────

export const PAY_BY_LABEL = "Pay by";

export const PAY_BY_NOTE =
	"Set by X, not by this console. X: «This setting can only be modified for line items using the APP_INSTALLS objective», and its default «is automatically set based upon the campaign objective and line item's bid unit».";

// ── Bid strategy ────────────────────────────────────────────────────

export const BID_LABEL = "Bid strategy";

export const BID_NAMES: Readonly<Record<BidStrategy, string>> = {
	AUTO: "Automatic bid — X: «automatically optimizes bidding based on daily budget and campaign flight dates»",
	MAX: "Maximum bid — X: «sets the maximum allowable bid»",
	TARGET:
		"Target cost — X: «attempts to make daily bid averages within 20% of the specified bid_amount_local_micro»",
};

export const BID_AMOUNT_LABEL = "Bid amount";

export function bidAmountHelp(currency: string | null): string {
	const unit = currency ?? "the funding instrument's currency";
	return `In ${unit}. Required for a maximum bid or a target cost — X: «Only values greater than zero are accepted.» Under an automatic bid no amount is sent: X says it «will be ignored».`;
}

/** Why the target cost is not in the list under ENGAGEMENTS. */
export const TARGET_NOT_FOR_ENGAGEMENTS =
	"Target cost is not offered under ENGAGEMENTS: X names the objectives it works for in three places — the reference's create, its update and the guide — and ENGAGEMENTS is in none of them.";

/**
 * 🔴 An open question, said as one. Under WEBSITE_CLICKS with a target cost, X's reference
 * says the default pay-by for the LINK_CLICKS goal («IMPRESSION is the default value») «is not
 * supported when setting TARGET», and that pay-by can only be changed under APP_INSTALLS.
 * What was SEEN differs from the reference's default: under AUTO X set pay_by LINK_CLICK, not
 * IMPRESSION, on xzomg (`audits/2026-10-23/e2e/log.txt:169`). Under TARGET nothing is seen yet.
 */
export const TARGET_PAY_BY_OPEN =
	"Not yet observed under a target cost. Under an automatic bid X set «Pay by» to link clicks (LINK_CLICK) on this account's WEBSITE_CLICKS ad group — not IMPRESSION, which its reference names as the default — and this console cannot set «Pay by». What X does under a target cost has not been seen yet.";

/** Beside the amount, when it is above the campaign's daily or total budget. */
export function bidOverBudget(which: readonly ("daily" | "total")[]): string {
	return `The bid is above the campaign's ${which.join(" and ")} budget. X: «the line item's bid_amount_local_micro value should not exceed daily_budget_amount_local_micro or total_budget_amount_local_micro of the parent campaign. Setting these values incorrectly may put the overall campaign into a paused and unservable state.»`;
}

// ── Placements ──────────────────────────────────────────────────────

export const PLACEMENTS_LABEL = "Placements";

export const PLACEMENT_NAMES: Readonly<Record<Placement, string>> = {
	ALL_ON_TWITTER: "All placements on X",
	TWITTER_TIMELINE: "Home timelines",
	TWITTER_PROFILE: "Profiles and tweet details",
	TWITTER_SEARCH: "Search results",
};

export const PLACEMENTS_HELP =
	"Chosen once: X's reference has no placements parameter on a line item's update, so they cannot be changed after the ad group is created. «All placements on X» goes on its own here — whether X takes it beside another placement is not in its documentation.";

/**
 * What each placement does with the audience step's choices — G:483–485, the guide's
 * targeting-by-placement list, in its own words where they matter.
 */
export const PLACEMENT_TARGETING: Readonly<Partial<Record<Placement, string>>> = {
	TWITTER_SEARCH:
		"Search needs at least one keyword (X: «TWITTER_SEARCH requires keyword targeting»). On Search X lists «Keyword Types (All)» and no interests or follower look-alikes.",
	TWITTER_TIMELINE:
		"On home timelines X lists interests, follower look-alikes and «Non-exact Keyword Types» — an exact keyword does not apply there.",
	TWITTER_PROFILE:
		"On profiles X lists interests, follower look-alikes and «Non-exact Keyword Types» — an exact keyword does not apply there.",
};

export const PLACEMENT_PROBLEMS: Readonly<Record<PlacementProblem, string>> = {
	"placements-empty": "Choose at least one placement.",
	"placements-all-alone":
		"«All placements on X» goes on its own: choose it alone, or choose the placements one by one.",
	"placements-profile-alone":
		"Profiles cannot be the only placement — X: «It is not possible to specify only TWITTER_PROFILE placement.» Add another.",
};

/**
 * On the audience step, when Search was chosen and no keyword was added. Since PR 4 the
 * keyword field is on that step, so the sentence asks for one there.
 */
export const SEARCH_NEEDS_KEYWORD =
	"Search results was chosen as a placement, and X says «TWITTER_SEARCH requires keyword targeting». Add a keyword under Advanced targeting below, or go back and remove Search.";

// ── Product, and the review step ────────────────────────────────────

/** What stays fixed on the ad-group step once placements and the bid are choices. */
export const PRODUCT_FIXED =
	"Product PROMOTED_TWEETS — fixed in v1, as in X's own example. Other products need fields v1 does not carry.";

/** The review line's goal: the one chosen, or that none is sent. */
export function goalSummary(goal: string): string {
	return goal === "" ? "goal: X's default for the objective (not sent)" : `goal ${goal}`;
}

/** The review line's bid: AUTO says the price is X's; MAX and TARGET name the amount. */
export function bidSummary(
	strategy: BidStrategy,
	micro: number | null,
	currency: string | null,
): string {
	if (strategy === "AUTO") return "automatic bid (X sets the price)";
	const amount = micro === null ? "no amount yet" : microMoney(micro, currency);
	return strategy === "MAX" ? `maximum bid ${amount}` : `target cost ${amount}`;
}

/** The review line's placements, by the names the step shows. */
export function placementsSummary(placements: readonly Placement[]): string {
	return placements.length === 0
		? "no placement chosen"
		: placements.map((p) => PLACEMENT_NAMES[p]).join(", ");
}
